import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReplyIcon from '@mui/icons-material/Reply';
import PaletteIcon from '@mui/icons-material/Palette';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { EXTRA_SEAT_MONTHLY, PLAN_PRICE, PRO_INCLUDED_SEATS } from '../../Shared/pricing';

export { EXTRA_SEAT_MONTHLY, PLAN_PRICE, PRO_INCLUDED_SEATS } from '../../Shared/pricing';

export const LANDING_TITLE =
	'Bidzbase — Proposals that follow themselves up for subcontractors';
export const LANDING_DESCRIPTION =
	'Send a branded proposal, get one-click accept or decline by email, and let Bidzbase chase open bids and unpaid invoices for you. Deposits, invoices, and payments — $25/mo for the full bid-to-invoice loop.';

export const TESTIMONIALS = [
	{
		name: 'Mike R.',
		trade: 'Welding shop owner, Colorado',
		quote:
			'I used to text “did you see the bid?” every other day. Now Bidzbase sends the reminder, they click accept, and I get the email before I put my phone down.',
	},
	{
		name: 'Danielle K.',
		trade: 'HVAC contractor, Texas',
		quote:
			'Jobber felt like overkill for what we do. Bidzbase does the bid-to-invoice loop and actually follows up for me. My customers love the one-click yes or no — no account, no app.',
	},
	{
		name: 'Tom S.',
		trade: 'Electrical subcontractor, Ohio',
		quote:
			'Deposits on acceptance alone saved us twice already. Customer approves the bid, gets a deposit link, pays online — I am not playing phone tag about half up front anymore.',
	},
];

export const FEATURES = [
	{
		icon: NotificationsActiveIcon,
		title: 'Automated bid & invoice follow-ups',
		description:
			'Pending proposals get a reminder on your cadence until they accept, decline, or expire. Unpaid invoices get the same treatment until the balance is paid. You set the interval — default every 2 days — or turn them off.',
	},
	{
		icon: ReplyIcon,
		title: 'One-click accept or decline',
		description:
			'Customers reply from the email. Acceptance is legally recorded with an audit trail. You get notified instantly — no phone tag, no lost paperwork.',
	},
	{
		icon: AccountBalanceWalletIcon,
		title: 'Deposit the moment they say yes',
		description:
			'Collect a deposit when a bid is approved, then invoice the balance on completion. The deposit email goes out automatically — common in trades, built in.',
	},
	{
		icon: PaymentIcon,
		title: 'Get paid your way',
		description:
			'Connect Stripe for card payments, or use check, mail, and Venmo on invoices. All included on Basic — no upsell to unlock billing.',
	},
	{
		icon: PaletteIcon,
		title: 'Your brand on every document',
		description:
			'Proposals, reminders, and invoices go out with your company name, logo, and payee details. Your customer never sees Bidzbase.',
	},
	{
		icon: AssignmentIcon,
		title: 'Bids, change orders & jobs in one place',
		description:
			'Customers, jobs, revisions, and file uploads from first estimate through closeout — built for subcontractor workflows, not a generic CRM.',
	},
];

export const FOLLOW_UP_BEATS = [
	{
		pain: 'Texting “did you see the bid?”',
		win: 'Automatic proposal reminders until they accept, decline, or it expires',
	},
	{
		pain: 'Hoping they pay the invoice',
		win: 'Automatic unpaid-invoice reminders until the balance is paid',
	},
	{
		pain: 'Phone tag for the deposit',
		win: 'Deposit instructions go out the moment they click Accept',
	},
	{
		pain: 'Making customers create an account',
		win: 'They reply from email. No login, no app, no friction.',
	},
];

export const FOLLOW_UP_TIMELINE = [
	{
		when: 'Day 0',
		title: 'You send the proposal',
		detail:
			'Branded PDF, one-click Accept / Decline, expiration date. You are done chasing.',
	},
	{
		when: 'Every 2 days',
		title: 'Bidzbase follows up',
		detail:
			'A reminder asks them to review the proposal and notes when it expires. Cadence is yours — 2 days by default, or off.',
	},
	{
		when: 'They accept',
		title: 'Deposit goes out automatically',
		detail:
			'Card, check, or Venmo. You get notified. The job moves forward without a phone call.',
	},
	{
		when: 'You invoice',
		title: 'Unpaid invoices get the same treatment',
		detail:
			'Reminders keep going on your cadence until the balance is paid. You still look professional — not naggy.',
	},
];

export const STEPS = [
	{
		step: '01',
		title: 'Build the proposal',
		description:
			'Add the customer, scope the work, and itemize the estimate. Set a deposit if you collect one up front.',
	},
	{
		step: '02',
		title: 'Send it — Bidzbase follows up',
		description:
			'Email a branded PDF with one-click accept or decline. Reminders go out on your cadence until they answer or the bid expires.',
	},
	{
		step: '03',
		title: 'Win the work and get paid',
		description:
			'Accept records legally. Deposit email goes out. Invoice the balance — unpaid invoices get automatic reminders too.',
	},
];

export const PRICING_TIERS = [
	{
		id: 'basic',
		name: 'Basic',
		price: PLAN_PRICE.basic,
		tagline: 'The complete bid-to-invoice product',
		signupTagline: 'Everything you need to bid and get paid',
		highlighted: true,
		signupAvailable: true,
		features: [
			'Unlimited bids, invoices & change orders',
			'One-click accept / decline from email',
			'Automatic bid follow-ups until they answer',
			'Automatic invoice follow-ups until they pay',
			'Deposit collected on acceptance',
			'Stripe, check, mail & Venmo',
			'Branded PDFs — your logo, not ours',
			'Customers never need an account',
			'Custom job statuses & file uploads',
			'Legally recorded acceptance',
		],
	},
	{
		id: 'pro',
		name: 'Pro',
		price: PLAN_PRICE.pro,
		tagline: 'Operations for growing shops',
		signupTagline: 'Time tracking, scheduling & crew tools',
		highlighted: false,
		signupAvailable: true,
		features: [
			'Everything in Basic',
			`Team accounts (up to ${PRO_INCLUDED_SEATS} seats)`,
			`Extra seats $${EXTRA_SEAT_MONTHLY}/mo each`,
			'Time tracking with geofence',
			'Scheduling & dispatch',
			'Analytics dashboard',
			'Manager SMS alerts',
		],
	},
];

/** Rows for the post-trial plan picker. `lostOnBasic` nudges toward Pro. */
export const PLAN_COMPARISON_ROWS = [
	{ label: 'Unlimited bids, invoices & branded PDFs', basic: true, pro: true },
	{
		label: 'Automatic bid & invoice follow-ups',
		basic: true,
		pro: true,
	},
	{ label: 'Deposits and email accept/decline', basic: true, pro: true },
	{
		label: 'Text alerts when a bid is accepted or expires',
		basic: false,
		pro: true,
		lostOnBasic: true,
	},
	{
		label: 'Scheduling & dispatch',
		basic: false,
		pro: true,
		lostOnBasic: true,
	},
	{
		label: 'Analytics dashboard',
		basic: false,
		pro: true,
		lostOnBasic: true,
	},
	{
		label: `Team accounts (up to ${PRO_INCLUDED_SEATS} seats)`,
		basic: false,
		pro: true,
	},
	{ label: 'Time tracking & location services', basic: false, pro: true },
	{
		label: 'All manager SMS alerts (schedule, clock, geofence)',
		basic: false,
		pro: true,
	},
];

export const FAQ_ITEMS = [
	{
		question: 'Do I have to chase bids and invoices myself?',
		answer:
			'No. Bidzbase emails the customer a reminder on the cadence you set (every 2 days by default) until a pending proposal is accepted, declined, or expires — and until an unpaid invoice is paid. You can turn either reminder off in Settings. Your shop is BCC’d so you see every follow-up.',
	},
	{
		question: 'Do my customers need an account?',
		answer:
			'No. Customers receive a branded PDF by email with one-click accept or decline links. Reminders and invoices work the same way. No login, no app download, no friction.',
	},
	{
		question: 'Is Stripe required?',
		answer:
			'No. Basic includes Stripe if you want card payments, but check, mail, and Venmo work too. Use whatever your customers prefer.',
	},
	{
		question: 'What is included in Basic?',
		answer:
			'The full bid-to-invoice loop at $25/mo: unlimited bids and invoices, itemized proposals, automatic bid and invoice follow-ups, deposits on acceptance, Stripe and Venmo, custom job statuses, branded PDFs, and legally recorded email acceptance. Basic is the product — not a teaser tier.',
	},
	{
		question: 'What is included in Pro?',
		answer:
			'Pro includes everything in Basic, plus operations tools for growing shops — team seats, time tracking, scheduling, analytics, and manager SMS alerts. Your 30-day trial includes a taste of Pro (bid SMS, scheduling, and analytics) so you can see the difference before you pick a plan.',
	},
	{
		question: 'What trades is Bidzbase for?',
		answer:
			'Subcontractors and small trade shops — welding, HVAC, electrical, remodeling, plumbing, and similar trades that live in bids and proposals.',
	},
	{
		question: 'How is this different from Jobber or other field-service tools?',
		answer:
			'Bidzbase focuses on the bid-to-invoice loop without enterprise complexity. You send the proposal; we follow up; they accept in the email; you collect a deposit and invoice. Basic is the complete core product at $25/mo. Pro adds crew ops when you need them — not before.',
	},
	{
		question: 'Can I try it before paying?',
		answer:
			'Yes. Sign up with no credit card for a 30-day free trial. The trial includes the full Basic product plus bid text alerts, scheduling, and analytics. When the trial ends, choose Basic or Pro — or deactivate the account. Paid plans are billed on the 1st of the month and prorated if you start mid-month.',
	},
	{
		question: 'What happens to my data if I cancel?',
		answer:
			'Your data stays available until the end of your billing period. After cancellation, you have a 30-day window to export before data is deleted.',
	},
];

export const scrollToSection = (id) => {
	const element = document.getElementById(id);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}
};

/** Text/icons on always-dark brand panels (hero, auth side panel, footer). */
export const ON_DARK = {
	text: '#F5F5F7',
	muted: 'rgba(245, 245, 247, 0.88)',
	soft: 'rgba(245, 245, 247, 0.8)',
	faint: 'rgba(245, 245, 247, 0.65)',
};

export const brandPanelGradient = (mode) =>
	mode === 'light'
		? 'linear-gradient(145deg, #2A4A5E 0%, #356380 48%, #4A7A9C 100%)'
		: 'linear-gradient(145deg, #0A0A0F 0%, #1C1C1E 48%, #2A4A5E 100%)';
